"use client";

import { useMemo, useState } from "react";
import { AlertTriangle, Layers } from "lucide-react";
import { ProductCard } from "@/components/product/ProductCard";
import { getDisplayPrice } from "@/services/pricingService";
import {
  PRODUCT_TYPES,
} from "@/utils/productType";
import { useAuth } from "@/context/AuthContext";

const LinkedAlternatives = ({ alternatives = [] }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("all");

  const sortedAlternatives = useMemo(() => {
    const items = Array.isArray(alternatives) ? alternatives : [];
    return items
      .map((product) => {
        const pricing = getDisplayPrice(product, user);
        const inStock = (product?.stockQty || 0) > 0;
        const price = Number(pricing?.price || 0);

        return {
          product,
          pricing,
          inStock,
          price,
        };
      })
      .sort((a, b) => {
        if (a.inStock !== b.inStock) return a.inStock ? -1 : 1;
        if (a.price !== b.price) return a.price - b.price;
        return String(a.product?.name || "").localeCompare(String(b.product?.name || ""));
      });
  }, [alternatives, user]);

  const alternativeCounts = useMemo(() => {
    const counts = { all: 0, aftermarket: 0, oes: 0 };
    sortedAlternatives.forEach(({ product }) => {
      counts.all += 1;
      if (product?.productType === PRODUCT_TYPES.OES) {
        counts.oes += 1;
        return;
      }
      counts.aftermarket += 1;
    });
    return counts;
  }, [sortedAlternatives]);

  const visibleAlternatives = useMemo(() => {
    if (activeTab === "all") {
      return sortedAlternatives;
    }
    if (activeTab === "oes") {
      return sortedAlternatives.filter(({ product }) => product?.productType === PRODUCT_TYPES.OES);
    }
    if (activeTab === "aftermarket") {
      return sortedAlternatives.filter(({ product }) => product?.productType !== PRODUCT_TYPES.OES);
    }
    return sortedAlternatives;
  }, [activeTab, sortedAlternatives]);

  if (!sortedAlternatives.length) return null;

  return (
    <div className="my-12 animate-fade-in rounded-2xl border border-slate-200 bg-white/70 p-5 md:p-7">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <div className="rounded-lg bg-slate-900 p-1.5">
              <Layers className="h-4 w-4 text-white" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">
              Linked Items
            </h2>
          </div>
          <p className="max-w-2xl text-sm text-slate-500">
            Review all products linked to this OEM, including OES and aftermarket copies.
          </p>
        </div>

        <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 p-1 shadow-sm">
          <button
            type="button"
            onClick={() => setActiveTab("all")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === "all"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-900"
            }`}
          >
            All Linked
            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-500">
              {alternativeCounts.all}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("aftermarket")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === "aftermarket"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-900"
            }`}
          >
            Aftermarket
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
              {alternativeCounts.aftermarket}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("oes")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === "oes"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-900"
            }`}
          >
            OES
            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-500">
              {alternativeCounts.oes}
            </span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {visibleAlternatives.map(({ product }) => {
          return (
            <ProductCard
              key={product._id || product.id}
              product={product}
            />
          );
        })}
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-[11px] font-medium leading-relaxed text-amber-800">
          Linked alternatives are shown for convenience and may not be exact replacements.
        </p>
      </div>
    </div>
  );
};

export default LinkedAlternatives;
